import { useTranslate } from "@tolgee/react";
import { useCallback, useMemo } from "react";
import { uniq } from "lodash-es";
import {
  organisationRoleNames,
  projectRoleNames,
  type OrganisationRole,
  type ProjectRole,
} from "./roles.generated.js";

// The role unions and name lists are generated from the database roles
// (`pnpm permissions roles generate`, MEIS-13076) — a role added via
// `pnpm permissions role add` reaches these hooks without hand edits.
// Labels stay Tolgee-translated (namespaces OrganisationRole / ProjectRole,
// keys = internal names, maintained by the same CLI).
export type { OrganisationRole, ProjectRole };

export function useProjectRoles() {
  const { t } = useTranslate("ProjectRole");

  const projectRoleLabels: Record<ProjectRole, string> = useMemo(
    () =>
      projectRoleNames.reduce(
        (labels, name) => {
          labels[name] = t(name);
          return labels;
        },
        {} as Record<ProjectRole, string>
      ),
    [t]
  );

  const getProjectRoleOptions = useCallback(
    (
      roles?:
        | {
            __typename?: "ProjectRole";
            id: string;
            internalName: string;
            label: string;
          }[]
        | undefined
        | null
    ): { value: string; label: string; internalName: string }[] => {
      return (
        roles
          ?.filter(
            role =>
              role?.internalName &&
              projectRoleLabels[role.internalName as ProjectRole]
          )
          .map(role => ({
            value: role.id,
            label: projectRoleLabels[role.internalName as ProjectRole],
            internalName: role.internalName,
          })) ?? []
      );
    },
    [projectRoleLabels]
  );

  const getProjectRoleTitle = useCallback(
    (
      roles?:
        | {
            __typename?: "ProjectRole";
            internalName: string;
          }[]
        | undefined
        | null
    ): string => {
      return uniq(
        roles
          ?.filter(
            role =>
              role?.internalName &&
              projectRoleLabels[role.internalName as ProjectRole]
          )
          .map(role => projectRoleLabels[role.internalName as ProjectRole]) ??
          []
      ).join(", ");
    },
    [projectRoleLabels]
  );

  return { getProjectRoleTitle, getProjectRoleOptions };
}

export function useOrganisationRoles() {
  const { t } = useTranslate("OrganisationRole");

  const organisationRoleLabels: Record<OrganisationRole, string> = useMemo(
    () =>
      organisationRoleNames.reduce(
        (labels, name) => {
          labels[name] = t(name);
          return labels;
        },
        {} as Record<OrganisationRole, string>
      ),
    [t]
  );

  const getOrganisationRoleOptions = useCallback(
    (
      roles?:
        | {
            __typename?: "OrganisationRole";
            id: string;
            internalName: string;
            label: string;
          }[]
        | null
        | undefined
    ): { value: string; label: string; internalName: string }[] => {
      return (
        roles
          ?.filter(
            role =>
              role?.internalName &&
              organisationRoleLabels[role.internalName as OrganisationRole]
          )
          .map(role => ({
            value: role.id,
            label:
              organisationRoleLabels[role.internalName as OrganisationRole],
            internalName: role.internalName,
          })) ?? []
      );
    },
    [organisationRoleLabels]
  );

  const getOrganisationRoleTitle = useCallback(
    (
      roles?:
        | {
            __typename?: "OrganisationRole";
            internalName: string;
          }[]
        | null
        | undefined
    ) => {
      if (!roles || roles?.length === 0) {
        return "-";
      }

      return uniq(
        roles
          ?.filter(
            role =>
              role?.internalName &&
              organisationRoleLabels[role.internalName as OrganisationRole]
          )
          .map(
            role =>
              organisationRoleLabels[role.internalName as OrganisationRole]
          ) ?? []
      ).join(", ");
    },
    [organisationRoleLabels]
  );

  return {
    getOrganisationRoleTitle,
    getOrganisationRoleOptions,
  };
}
